import React, { useCallback } from 'react';
import {
  type GestureResponderEvent,
  StyleSheet,
  TouchableOpacity,
  type TouchableOpacityProps,
  type ViewProps,
  type ViewStyle,
} from 'react-native';

import { theme } from '@garnish/constants';

import { HapticFeedbackStyle, triggerHapticFeedback } from '../../../../../../utils';

// ─────────────────────────────────────────────────────────────────────────────

export const ScreenHeaderButtonContainer = (props: Props) => {
  const { children, disabled, onPress, palette = 'translucent', style, ...restProps } = props;

  // ─── Handlers ───────────────────────────────────────────────────────

  const handlePress = useCallback(
    (event: GestureResponderEvent) => {
      void triggerHapticFeedback(HapticFeedbackStyle.Light);
      onPress?.(event);
    },
    [onPress],
  );

  // ─── Styles ──────────────────────────────────────────────────────────

  const containerStyles = [
    styles.container,
    paletteStyles[palette],
    disabled ? styles.disabledContainer : undefined,
    style,
  ];

  // ─────────────────────────────────────────────────────────────────────

  return (
    <TouchableOpacity
      accessibilityRole="button"
      activeOpacity={0.8}
      disabled={disabled}
      onPress={handlePress}
      style={containerStyles}
      {...restProps}
    >
      {children}
    </TouchableOpacity>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    height: 40,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    borderRadius: 1000,
    paddingHorizontal: theme.spacing['4'],
  },
  disabledContainer: {
    opacity: 0.5,
  },
});

const paletteStyles = StyleSheet.create<Record<ScreenHeaderButtonContainerPalette, ViewStyle>>({
  light: { backgroundColor: theme.colors.WHITE },
  translucent: { backgroundColor: theme.colors.OPACITY.WHITE.LIGHT },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  palette?: ScreenHeaderButtonContainerPalette;
  style?: ViewProps['style'];
} & Omit<TouchableOpacityProps, 'style'>;

type ScreenHeaderButtonContainerPalette = 'light' | 'translucent';
